import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class } from "../models/class";
import { PROFILE_APPROVAL_STATUS } from "../models/constants/model-constants";
import { Course } from "../models/course";
import { School } from "../models/school";
import { Semester } from "../models/semester";
import { Student } from "../models/student";
import { Teacher } from "../models/teacher";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

class SchoolController {
  /**
   * Get all schools (name and code only) - Guest endpoint
   * Returns all approved and active schools for public access
   * No pagination to keep it simple for dropdown/selection purposes
   */
  async getAllSchools(req: Request, res: Response): Promise<void> {
    try {
      logger.info("Guest request for all schools list", {
        ip: req.ip,
        userAgent: req.get("User-Agent"),
      });

      // Find all active schools (approved, verified, and not suspended)
      // Active schools are those with status "approved", isVerified true, and not suspended
      const schools = await School.find(
        {
          status: "approved", // Only approved schools
          isVerified: true, // Only verified schools
          // Explicitly exclude suspended schools (status should not be suspended)
          // Note: status is already filtered to "approved" above, so suspended schools are excluded
        },
        {
          schoolName: 1, // Include school name
          schoolCode: 1, // Include school code
          _id: 1, // Include _id for dropdown value
        },
      ).sort({ schoolName: 1 }); // Sort alphabetically by school name

      // Transform to include id field for dropdown compatibility
      const schoolsForDropdown = schools.map(school => ({
        id: String(school._id),
        name: school.schoolName,
        code: school.schoolCode || "",
      }));

      const totalCount = schoolsForDropdown.length;

      logger.info(`Retrieved ${totalCount} schools for guest access`);

      res.status(200).json({
        success: true,
        message:
          totalCount > 0
            ? `${totalCount} schools found`
            : "No schools available",
        data: {
          schools: schoolsForDropdown,
          totalCount,
          timestamp: new Date().toISOString(),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error retrieving schools list:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve schools list",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Search schools by name or code - Guest endpoint
   * Allows guests to search for specific schools
   */
  async searchSchools(req: Request, res: Response): Promise<void> {
    try {
      const { query } = req.query;

      if (!query || typeof query !== "string" || query.trim().length < 2) {
        res.status(400).json({
          success: false,
          message: "Search query must be at least 2 characters long",
          error: "Invalid query parameter",
        } as APIResponse);
        return;
      }

      const searchQuery = query.trim();
      logger.info(`Guest search for schools with query: "${searchQuery}"`, {
        ip: req.ip,
        userAgent: req.get("User-Agent"),
      });

      // Create case-insensitive regex for search
      const searchRegex = new RegExp(searchQuery, "i");

      const schools = await School.find(
        {
          status: "approved",
          isVerified: true,
          $or: [{ schoolName: searchRegex }],
        },
        {
          schoolName: 1,
          _id: 0,
        },
      )
        .sort({ schoolName: 1 })
        .limit(50); // Limit to 50 results for performance

      const totalCount = schools.length;

      logger.info(
        `Search returned ${totalCount} schools for query: "${searchQuery}"`,
      );

      res.status(200).json({
        success: true,
        message:
          totalCount > 0
            ? `${totalCount} schools found matching "${searchQuery}"`
            : `No schools found matching "${searchQuery}"`,
        data: {
          schools,
          totalCount,
          searchQuery,
          timestamp: new Date().toISOString(),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error searching schools:", error);
      res.status(500).json({
        success: false,
        message: "Failed to search schools",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all subjects (courses) for a school - Student/Teacher onboarding, Class creation
   * Returns Courses from the catalog. Used for "Major Subjects" dropdown, "Select Course" dropdown.
   * @route GET /api/v1/schools/:schoolId/subjects
   * @query semesterId (optional) - filter by semester
   * @access Private (authenticated)
   */
  async getSchoolSubjects(req: Request, res: Response): Promise<void> {
    try {
      const { schoolId } = req.params;
      const { semesterId } = req.query;

      if (!schoolId || typeof schoolId !== "string" || schoolId.trim() === "") {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid school ID parameter",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(schoolId)) {
        res.status(400).json({
          success: false,
          message: "Invalid school ID format",
          error: "Invalid school ID",
        } as APIResponse);
        return;
      }

      const schoolObjectId = new mongoose.Types.ObjectId(schoolId);

      // Verify school exists
      const schoolExists = await School.exists({ _id: schoolObjectId });
      if (!schoolExists) {
        res.status(404).json({
          success: false,
          message: `School with ID '${schoolId}' not found`,
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Fetch Courses from catalog (subjects = courses)
      const courseFilter: any = { schoolId: schoolObjectId, isActive: true };
      if (
        semesterId &&
        typeof semesterId === "string" &&
        mongoose.Types.ObjectId.isValid(semesterId)
      ) {
        courseFilter.semesterId = new mongoose.Types.ObjectId(semesterId);
      }

      const courses = await Course.find({ ...courseFilter })
        .select(
          "_id courseCode courseName description credits department level courseType",
        )
        .populate("semesterId", "semesterName academicYear")
        .sort({ courseCode: 1 })
        .lean();

      const subjects = courses.map(course => ({
        // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
        id: String(course._id),
        name: course.courseName || course.courseCode || "",
        code: course.courseCode || "",
        description: course.description,
        credits: course.credits,
        department: course.department,
        level: course.level,
        courseType: course.courseType,
        ...(course.semesterId && {
          semester: (course.semesterId as { semesterName?: string })
            ?.semesterName,
        }),
      }));

      const totalCount = subjects.length;

      res.status(200).json({
        success: true,
        message:
          totalCount > 0
            ? `${totalCount} subjects found`
            : "No subjects available",
        data: {
          subjects,
          totalCount,
          timestamp: new Date().toISOString(),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school subjects:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch school subjects",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get school details by ID - Guest endpoint
   * Returns basic school information for a specific school ID
   */
  async getSchoolById(req: Request, res: Response): Promise<void> {
    try {
      const { schoolId } = req.params;

      if (!schoolId || typeof schoolId !== "string") {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid school ID parameter",
        } as APIResponse);
        return;
      }

      logger.info(`Guest request for school details: ${schoolId}`, {
        ip: req.ip,
        userAgent: req.get("User-Agent"),
      });

      const school = await School.findOne(
        {
          _id: schoolId,
          status: "approved",
          isVerified: true,
        },
        {
          schoolName: 1,
          establishmentYear: 1,
          description: 1,
          phone: 1,
          website: 1,
          email: 1,
          "address.city": 1,
          "address.state": 1,
          "address.country": 1,
          totalStudents: 1,
          totalTeachers: 1,
          _id: 0,
        },
      );

      if (!school) {
        res.status(404).json({
          success: false,
          message: `School with ID '${schoolId}' not found or not available`,
          error: "School not found",
        } as APIResponse);
        return;
      }

      logger.info(`School details retrieved for ID: ${schoolId}`);

      res.status(200).json({
        success: true,
        message: `School details for ${schoolId}`,
        data: {
          school,
          timestamp: new Date().toISOString(),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error retrieving school details:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve school details",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get comprehensive school dashboard with pending requests
   * @route GET /api/v1/school/dashboard
   * @access Private (School Admin)
   */
  async getDashboard(req: Request, res: Response): Promise<void> {
    try {
      const schoolId = (req as any).user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const schoolObjectId =
        mongoose.Types.ObjectId.createFromHexString(schoolId);

      // Get school basic info
      const school = await School.findById(schoolObjectId).select(
        "schoolName establishmentYear totalTeachers totalStudents status isVerified",
      );

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School not found",
          error: "School not found",
        } as APIResponse);
        return;
      }

      // Get comprehensive statistics
      const [
        totalTeachers,
        approvedTeachers,
        pendingTeachers,
        totalStudents,
        approvedStudents,
        pendingStudents,
        totalClasses,
        activeClasses,
        currentSemester,
        recentTeachers,
        recentStudents,
        pendingTeacherRequests,
        pendingStudentRequests,
      ] = await Promise.all([
        // Teacher counts
        Teacher.countDocuments({ schoolId: schoolObjectId }),
        Teacher.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.APPROVED,
        }),
        Teacher.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        }),

        // Student counts
        Student.countDocuments({ schoolId: schoolObjectId }),
        Student.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.APPROVED,
        }),
        Student.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        }),

        // Class counts
        Class.countDocuments({ schoolId: schoolObjectId }),
        Class.countDocuments({
          schoolId: schoolObjectId,
          status: "active",
        }),

        // Current semester
        Semester.findCurrent(schoolObjectId),

        // Recent teachers (last 5)
        Teacher.find({ schoolId: schoolObjectId })
          .sort({ createdAt: -1 })
          .limit(5)
          .select("fullName employeeId status approvalStatus createdAt")
          .populate("userId", "email"),

        // Recent students (last 5)
        Student.find({ schoolId: schoolObjectId })
          .sort({ createdAt: -1 })
          .limit(5)
          .select("fullName studentId status approvalStatus createdAt")
          .populate("userId", "email"),

        // Pending teachers (last 10)
        Teacher.find({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        })
          .sort({ createdAt: -1 })
          .limit(10)
          .select(
            "fullName employeeId email phone subjects createdAt profilePicture",
          )
          .populate("userId", "email"),

        // Pending students (last 10)
        Student.find({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        })
          .sort({ createdAt: -1 })
          .limit(10)
          .select(
            "fullName studentId email phone academicLevel createdAt profilePicture",
          )
          .populate("userId", "email"),
      ]);

      // Calculate approval rates
      const teacherApprovalRate =
        totalTeachers > 0 ? (approvedTeachers / totalTeachers) * 100 : 0;
      const studentApprovalRate =
        totalStudents > 0 ? (approvedStudents / totalStudents) * 100 : 0;

      res.status(200).json({
        success: true,
        message: "School dashboard data retrieved successfully",
        data: {
          school: {
            id: school._id,
            schoolName: school.schoolName,
            establishmentYear: school.establishmentYear,
            status: school.status,
            isVerified: school.isVerified,
          },
          statistics: {
            teachers: {
              total: totalTeachers,
              approved: approvedTeachers,
              pending: pendingTeachers,
              approvalRate: Math.round(teacherApprovalRate * 100) / 100,
            },
            students: {
              total: totalStudents,
              approved: approvedStudents,
              pending: pendingStudents,
              approvalRate: Math.round(studentApprovalRate * 100) / 100,
            },
            classes: {
              total: totalClasses,
              active: activeClasses,
            },
          },
          currentSemester: currentSemester
            ? {
                id: currentSemester._id,
                semesterName: currentSemester.semesterName,
                academicYear: currentSemester.academicYear,
                startDate: currentSemester.startDate,
                endDate: currentSemester.endDate,
                isActive: currentSemester.isActive,
              }
            : null,
          recentActivity: {
            teachers: recentTeachers.map(teacher => ({
              id: teacher._id,
              fullName: teacher.fullName,
              employeeId: teacher.employeeId,
              email: (teacher as any).userId?.email,
              status: teacher.status,
              approvalStatus: teacher.approvalStatus,
              createdAt: teacher.createdAt,
            })),
            students: recentStudents.map(student => ({
              id: student._id,
              fullName: student.fullName,
              studentId: student.studentId,
              email: (student as any).userId?.email,
              status: student.status,
              approvalStatus: student.approvalStatus,
              createdAt: student.createdAt,
            })),
          },
          pendingRequests: {
            teachers: pendingTeacherRequests.map(teacher => ({
              id: teacher._id,
              fullName: teacher.fullName,
              employeeId: teacher.employeeId,
              email: (teacher as any).userId?.email,
              phone: teacher.phone,
              subjects: teacher.subjects,
              profilePicture: teacher.profilePicture,
              createdAt: teacher.createdAt,
            })),
            students: pendingStudentRequests.map(student => ({
              id: student._id,
              fullName: student.fullName,
              studentId: student.studentId,
              email: (student as any).userId?.email,
              phone: student.phone,
              academicLevel: student.academicLevel,
              profilePicture: student.profilePicture,
              createdAt: student.createdAt,
            })),
          },
          summary: {
            totalPendingRequests: pendingTeachers + pendingStudents,
            needsAttention: pendingTeachers + pendingStudents > 0,
            lastUpdated: new Date().toISOString(),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school dashboard:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch school dashboard",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get quick statistics for school dashboard
   * @route GET /api/v1/school/dashboard/stats
   * @access Private (School Admin)
   */
  async getQuickStats(req: Request, res: Response): Promise<void> {
    try {
      const schoolId = (req as any).user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const schoolObjectId =
        mongoose.Types.ObjectId.createFromHexString(schoolId);

      const [
        pendingTeachers,
        pendingStudents,
        approvedTeachers,
        approvedStudents,
        activeClasses,
      ] = await Promise.all([
        Teacher.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        }),
        Student.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.PENDING,
        }),
        Teacher.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.APPROVED,
        }),
        Student.countDocuments({
          schoolId: schoolObjectId,
          approvalStatus: PROFILE_APPROVAL_STATUS.APPROVED,
        }),
        Class.countDocuments({
          schoolId: schoolObjectId,
          status: "active",
        }),
      ]);

      res.status(200).json({
        success: true,
        message: "Quick statistics retrieved successfully",
        data: {
          pendingRequests: {
            teachers: pendingTeachers,
            students: pendingStudents,
            total: pendingTeachers + pendingStudents,
          },
          approved: {
            teachers: approvedTeachers,
            students: approvedStudents,
            total: approvedTeachers + approvedStudents,
          },
          classes: {
            active: activeClasses,
          },
          lastUpdated: new Date().toISOString(),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching quick stats:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch quick statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const schoolController = new SchoolController();
